import { Button, Radio } from 'components/core';
import { getIconByNotificationType } from 'components/core/Notification';
import { useI18n } from 'contexts/i18n';
import getConfig from 'next/config';
import Image from 'next/legacy/image';
import { FC, useEffect } from 'react';
import { AiOutlinePlusCircle } from 'react-icons/ai';
import { useIteroPayments } from 'reactQuery/queries';
import { LoadingSpinner, LoadingWrapper } from 'styles/sharedStyles';
import { CreditCardClientData, ScannerSubFundingSource } from 'types';
import { NaOrderPaymentProps, Styled } from '.';
import {
  getCreditCardLogo,
  getSubFundingSourceByPaymentMethod,
  reorderCreditCards,
  shouldDisableSubmitPayment,
  shouldDisplayAddCardOption,
} from './ITeroOrderPayment.helper';
import { useReadOnlyUser } from 'utils/hooks';

const NaOrderPayment: FC<NaOrderPaymentProps> = ({
  mode,
  onSubmit,
  selectedCreditCard,
  setCreditCard,
  paymentType,
  tmSelectedScannerFundingSource,
  tmSelectedScannerSubFundingSource,
  selectedScannerSubFundingSource,
  setSelectedScannerSubFundingSource,
  financingCompany,
}) => {
  const { publicRuntimeConfig } = getConfig();
  const i18n = useI18n();
  const {
    data: paymentMethods,
    isLoading: isCreditCardLoading,
    isIdle,
    isError,
  } = useIteroPayments(
    paymentType,
    tmSelectedScannerFundingSource,
    selectedScannerSubFundingSource,
    financingCompany
  );
  const { isReadOnlyUser } = useReadOnlyUser();

  const invalidFundingSource =
    selectedScannerSubFundingSource === undefined ||
    tmSelectedScannerFundingSource === undefined;
  const showPaymentOptions = !invalidFundingSource;
  const cards = reorderCreditCards(paymentMethods?.cards ?? []);
  const otherPaymentMethods = paymentMethods?.others ?? [];

  useEffect(() => {
    // Set the default credit card.
    if (
      (paymentType === 'scanner' || paymentType === 'rental') &&
      paymentMethods &&
      paymentMethods.cards.length > 0 &&
      [
        'ACH (Account and Routing number)',
        'Credit Card (Full Payment)',
        '11 Month Deferral',
        'EPT',
        'NT4M',
        'NT6M',
        'NT30',
        'NT60',
        'NT90',
      ].includes(tmSelectedScannerSubFundingSource)
    ) {
      const defaultCreditCard = paymentMethods.cards.filter(
        card => card.default
      )[0];
      const achPayment = paymentMethods.cards.filter(
        card => card.cardType === 'ACH'
      )[0];
      if (
        tmSelectedScannerSubFundingSource === 'ACH (Account and Routing number)'
      ) {
        setCreditCard(achPayment);
      } else if (defaultCreditCard) {
        setCreditCard(defaultCreditCard);
      } else {
        setCreditCard(paymentMethods.cards[0]);
      }
    }

    // check & wire
    if (
      paymentType === 'scanner' &&
      paymentMethods &&
      paymentMethods.others.length > 0 &&
      typeof selectedCreditCard === 'undefined'
    ) {
      setSelectedScannerSubFundingSource(tmSelectedScannerSubFundingSource);
    }

    //3rd Party Financing
    if (
      paymentType === 'scanner' &&
      paymentMethods &&
      paymentMethods.others.length === 1 &&
      tmSelectedScannerFundingSource === '3rd Party Financing'
    ) {
      setSelectedScannerSubFundingSource('3rd Party Financing');
    }

    if (
      paymentType === 'subscription' &&
      paymentMethods &&
      paymentMethods.cards.length > 0 &&
      typeof selectedCreditCard === 'undefined'
    ) {
      const defaultCreditCard = paymentMethods.cards.filter(
        card => card.default
      )[0];
      if (defaultCreditCard) {
        setCreditCard(defaultCreditCard);
      } else {
        setCreditCard(paymentMethods.cards[0]);
      }
    }
  }, [paymentMethods]);

  if (isCreditCardLoading || isIdle) {
    return (
      <LoadingWrapper>
        <LoadingSpinner width="25px" height="25px" isPrimary={true} />
      </LoadingWrapper>
    );
  }

  const handleCreditCardChange = (creditCard: CreditCardClientData) => {
    setCreditCard(creditCard);
    if (paymentType === 'scanner' || paymentType === 'rental') {
      const subFundingSource = getSubFundingSourceByPaymentMethod(
        tmSelectedScannerFundingSource,
        tmSelectedScannerSubFundingSource,
        creditCard,
        selectedScannerSubFundingSource
      );
      setSelectedScannerSubFundingSource(subFundingSource);
    }
  };

  const handleOtherPaymentMethods = (
    selectedSubFundingSource: ScannerSubFundingSource
  ) => {
    setCreditCard(undefined);
    if (paymentType === 'scanner') {
      const subFundingSource = getSubFundingSourceByPaymentMethod(
        tmSelectedScannerFundingSource,
        tmSelectedScannerSubFundingSource,
        undefined,
        selectedSubFundingSource
      );
      setSelectedScannerSubFundingSource(subFundingSource);
    }
  };

  if (isError) {
    return (
      <>{i18n['Something went wrong while fetching credit card detail.']}</>
    );
  }

  return (
    <>
      {mode === 'create' && (
        <>
          {paymentType === 'scanner' && (
            <Styled.Notification>
              <Styled.NotificationIcon>
                {getIconByNotificationType('info')}
              </Styled.NotificationIcon>
              <Styled.NotificationBody>
                {
                  i18n[
                    'Your preferred method shall be used towards your scanner payment, processing fee, and ancillary products payment (if any)'
                  ]
                }
              </Styled.NotificationBody>
            </Styled.Notification>
          )}
          {paymentType === 'subscription' && (
            <Styled.Notification>
              <Styled.NotificationIcon>
                {getIconByNotificationType('info')}
              </Styled.NotificationIcon>
              <Styled.NotificationBody>
                {
                  i18n[
                    'Your payment method shall be applicable towards your future service term plan'
                  ]
                }
              </Styled.NotificationBody>
            </Styled.Notification>
          )}
          {paymentType === 'rental' && (
            <Styled.Notification>
              <Styled.NotificationIcon>
                {getIconByNotificationType('info')}
              </Styled.NotificationIcon>
              <Styled.NotificationBody>
                {
                  i18n[
                    'Your payment method shall be applicable towards your rental payments.'
                  ]
                }
              </Styled.NotificationBody>
            </Styled.Notification>
          )}

          {paymentType === 'scanner' &&
            ['NT4M', 'NT6M', 'NT30', 'NT60', 'NT90'].includes(
              selectedScannerSubFundingSource
            ) && (
              <Styled.EPTMessageContainer>
                {i18n['Please select your payment method for']}{' '}
                {selectedScannerSubFundingSource}
              </Styled.EPTMessageContainer>
            )}

          {paymentType === 'scanner' &&
            ['EPT'].includes(selectedScannerSubFundingSource) && (
              <Styled.EPTMessageContainer>
                {i18n['EPT TERMS MESSAGE']}
              </Styled.EPTMessageContainer>
            )}

          {paymentType === 'scanner' &&
            ['11 Month Deferral'].includes(selectedScannerSubFundingSource) && (
              <Styled.EPTMessageContainer data-testid="11-month-deferral-message">
                {i18n['11 MONTH DEFERRAL TERMS MESSAGE']}
              </Styled.EPTMessageContainer>
            )}

          {!showPaymentOptions && (
            <Styled.PayByInvoiceTitle error>
              {i18n['Itero Invalid Funding Source']}
            </Styled.PayByInvoiceTitle>
          )}

          {showPaymentOptions && (
            <>
              {cards?.map(creditCard => {
                const isSelected = selectedCreditCard?.id === creditCard.id;

                return (
                  <Styled.CreditCardContainer
                    key={creditCard.id}
                    isSelected={isSelected}
                    onClick={() => handleCreditCardChange(creditCard)}
                  >
                    <Styled.CreditCard>
                      <Radio
                        checked={isSelected}
                        onChange={() => handleCreditCardChange(creditCard)}
                      />

                      <Image
                        src={getCreditCardLogo(creditCard.cardType)}
                        alt="Card logo"
                        width={40}
                        height={40}
                      />

                      <Styled.CardHolderName>
                        {creditCard.name}
                      </Styled.CardHolderName>

                      <Styled.LastDigits>
                        {i18n['ending in']} {creditCard.last4Digit}
                      </Styled.LastDigits>

                      <Styled.Expiry>
                        {i18n['Exp:']} {creditCard.expiry}
                      </Styled.Expiry>
                    </Styled.CreditCard>
                  </Styled.CreditCardContainer>
                );
              })}

              {otherPaymentMethods.map(paymentOption => {
                const isSelected =
                  paymentOption.value === selectedScannerSubFundingSource;

                return (
                  <Styled.PayByInvoiceContainer
                    key={paymentOption.value}
                    isSelected={isSelected}
                    onClick={() =>
                      handleOtherPaymentMethods(paymentOption.value)
                    }
                  >
                    <Radio
                      checked={isSelected}
                      onChange={() =>
                        handleOtherPaymentMethods(paymentOption.value)
                      }
                    />

                    {paymentOption.logo && (
                      <Image
                        src={getCreditCardLogo(paymentOption.logo)}
                        alt="Payment logo"
                        width={24}
                        height={24}
                      />
                    )}

                    <Styled.PayByInvoiceTitle>
                      {paymentOption.label}
                    </Styled.PayByInvoiceTitle>
                  </Styled.PayByInvoiceContainer>
                );
              })}

              <Styled.ButtonWrapper>
                {shouldDisplayAddCardOption(
                  paymentType,
                  tmSelectedScannerFundingSource,
                  tmSelectedScannerSubFundingSource
                ) && (
                  <Button
                    variant="secondary"
                    disabled={isReadOnlyUser}
                    onClick={() =>
                      window.open(publicRuntimeConfig.PAYMENT_APP_URL, '_blank')
                    }
                  >
                    <AiOutlinePlusCircle />
                    <Styled.ButtonLabel>
                      {i18n['Add Payment Method']}
                    </Styled.ButtonLabel>
                  </Button>
                )}

                <Styled.ContinuetoReviewButton>
                  <Button
                    size="md"
                    onClick={onSubmit}
                    disabled={shouldDisableSubmitPayment(
                      paymentType,
                      paymentMethods.cards,
                      selectedCreditCard,
                      selectedScannerSubFundingSource
                    )}
                  >
                    {i18n['Next']}
                  </Button>
                </Styled.ContinuetoReviewButton>
              </Styled.ButtonWrapper>
            </>
          )}
        </>
      )}
      {mode === 'edit' && (
        <>
          {selectedCreditCard?.id && (
            <>
              {/* {paymentType === 'scanner' && (
                <Styled.SubfundingSourceContainer>
                  Payment Mode: <span>{selectedScannerSubFundingSource}</span>
                </Styled.SubfundingSourceContainer>
              )} */}
              <Styled.CreditCardReadOnly>
                <Image
                  src={getCreditCardLogo(selectedCreditCard.cardType)}
                  alt="logo"
                  width={40}
                  height={40}
                />
                <Styled.CardHolderName>
                  {selectedCreditCard.name}
                </Styled.CardHolderName>
                <Styled.LastDigits>
                  {`${i18n['ending in']} ${selectedCreditCard.last4Digit}`}{' '}
                </Styled.LastDigits>
                <Styled.Expiry>{`${i18n['Exp:']} ${selectedCreditCard.expiry}`}</Styled.Expiry>
              </Styled.CreditCardReadOnly>
            </>
          )}
          {!selectedCreditCard &&
            (selectedScannerSubFundingSource?.length ?? 0) > 0 && (
              <Styled.CreditCardReadOnly>
                <Styled.PayByInvoiceTitle>
                  {selectedScannerSubFundingSource}

                  {tmSelectedScannerFundingSource === '3rd Party Financing' && (
                    <span> ({financingCompany})</span>
                  )}
                </Styled.PayByInvoiceTitle>
              </Styled.CreditCardReadOnly>
            )}
        </>
      )}
    </>
  );
};

export default NaOrderPayment;
